import { HttpError, postJson } from '@/services/http';
import { mintRecaptchaToken, RecaptchaUnavailableError } from '@/services/recaptcha';

/**
 * Drives the "Vis kontaktinformasjon" buttons.
 *
 * The contact details are not in the page — not hidden, not encoded, nowhere.
 * The rot13 payload this replaces shipped them in every page's HTML, which
 * stopped a regex over the source and nothing that runs JavaScript. They are
 * now fetched from the reveal endpoint after a reCAPTCHA check, so crawlers
 * and scrapers never see them and the phone number can be published again
 * without becoming a permanent public record.
 */

interface RevealedDetails {
  owner: string | null;
  phone: string | null;
  email: string | null;
}

/** One reveal per page view: the second button reuses the first answer. */
let revealed: RevealedDetails | null = null;

export function bindRevealButtons(): void {
  document
    .querySelectorAll<HTMLButtonElement>('button[data-action="revealcontact"]')
    .forEach((button) => {
      button.addEventListener('click', () => {
        void onReveal(button);
      });
    });
}

async function onReveal(button: HTMLButtonElement): Promise<void> {
  const container = button.closest<HTMLElement>('.contact-info-content');

  if (!container) {
    return;
  }

  setStatus(container, '');
  const original = button.textContent;
  button.disabled = true;
  button.textContent = 'Henter..';

  try {
    revealed ??= await fetchDetails(container);
    fillEverySlot(revealed);
  } catch (error) {
    button.disabled = false;
    button.textContent = original;
    setStatus(container, describeFailure(error));
    console.error('Vis kontaktinformasjon feilet', error);
  }
}

async function fetchDetails(container: HTMLElement): Promise<RevealedDetails> {
  const url = container.dataset.revealUrl;

  if (!url) {
    throw new Error('data-reveal-url missing on the contact info container');
  }

  const recaptchaToken = await mintRecaptchaToken(
    container.dataset.recaptchaSiteKey || undefined,
    container.dataset.recaptchaAction ?? 'reveal_contact',
  );

  // The contact form partial is on every page through the modal, so its
  // antiforgery field is always available to borrow.
  const antiforgery = document.querySelector<HTMLInputElement>(
    "input[name='__RequestVerificationToken']",
  )?.value;
  const headers: Record<string, string> = antiforgery
    ? { RequestVerificationToken: antiforgery }
    : {};

  const details = await postJson<RevealedDetails>(url, { recaptchaToken }, headers);

  if (!details) {
    throw new Error('empty reveal response');
  }

  return details;
}

/**
 * Fills every slot on the page at once. The contact page and the modal both
 * carry a contact-info block, and revealing in one should reveal both.
 */
function fillEverySlot(details: RevealedDetails): void {
  if (details.owner) {
    // textContent, not innerHTML: these values come from configuration and
    // never need to carry markup.
    document
      .querySelectorAll<HTMLElement>('[data-content="ownerinbytes"]')
      .forEach((element) => {
        element.textContent = details.owner;
      });
  }

  fillLink('phoneinbytes', details.phone, (value) => `tel:${value.replace(/\s/g, '')}`);
  fillLink('emailinbytes', details.email, (value) => `mailto:${value}`);

  document
    .querySelectorAll<HTMLElement>('.contact-details')
    .forEach((element) => element.removeAttribute('hidden'));
  document.querySelectorAll<HTMLElement>('.reveal-contact').forEach((element) => element.remove());
}

function fillLink(
  kind: string,
  value: string | null,
  toHref: (value: string) => string,
): void {
  document
    .querySelectorAll<HTMLAnchorElement>(`a[data-content="${kind}"]`)
    .forEach((element) => {
      if (value) {
        element.textContent = value;
        element.href = toHref(value);
      } else {
        // The server renders a row only for a configured detail, so this is
        // defensive: an empty link is worse than no link.
        element.closest('tr')?.remove() ?? element.remove();
      }
    });
}

function setStatus(container: HTMLElement, message: string): void {
  // textContent, so a server or network message can never inject markup.
  const status = container.querySelector<HTMLElement>('.reveal-contact-status');
  status?.replaceChildren(document.createTextNode(message));
}

function describeFailure(error: unknown): string {
  if (error instanceof RecaptchaUnavailableError) {
    return 'Vi klarte ikke å laste robotsjekken. Last siden på nytt og prøv igjen, '
      + 'eller bruk kontaktskjemaet.';
  }

  if (error instanceof HttpError && error.status === 403) {
    return 'Vi klarte ikke å bekrefte at du er et menneske. Prøv gjerne igjen.';
  }

  if (error instanceof HttpError && error.status === 429) {
    return 'For mange forsøk. Vent litt og prøv igjen.';
  }

  return 'Beklager, det oppsto en feil. Prøv gjerne igjen, eller bruk kontaktskjemaet.';
}
